class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.messages = {
      processing: Theme?.products?.product?.processing || "Adding",
      success: Theme?.products?.product?.success || "Added to cart",
      error: Theme?.products?.product?.error || "Unable to add item to cart",
    };

    this.form = this.querySelector("form");
    this.submitButton = this.querySelector('input[type="submit"]');
    this.messageElement = this.querySelector(".message");
    this.originalButtonText = this.submitButton?.value || "Add to cart";

    if (this.form) {
      this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
    }
  }

  async onSubmitHandler(event) {
    event.preventDefault();

    if (this.submitButton.hasAttribute("disabled")) {
      return;
    }

    this.setLoadingState(true);

    const formData = new FormData(this.form);

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(formData),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.handleCartAddSuccess(data);
    } catch (error) {
      this.handleCartAddError(error);
    } finally {
      this.setLoadingState(false);
    }
  }

  setLoadingState(isLoading) {
    if (!this.submitButton) return;

    if (isLoading) {
      this.submitButton.setAttribute("disabled", "disabled");
      this.submitButton.value = this.messages.processing;
    } else {
      this.submitButton.removeAttribute("disabled");
      this.submitButton.value = this.originalButtonText;
    }
  }

  handleCartAddSuccess(response) {
    this.showMessage(this.messages.success, "success");

    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        bubbles: true,
      }),
    );
  }

  handleCartAddError(error) {
    let errorMessage = this.messages.error;

    // Try to extract error message from Shopify response
    try {
      if (error.message && error.message.includes("description")) {
        const errorData = JSON.parse(error.message);
        errorMessage = errorData.description || errorMessage;
      } else if (error.message && !error.message.includes("HTTP error")) {
        errorMessage = error.message;
      }
    } catch (e) {
      console.warn("Could not parse error message:", error.message);
    }

    this.showMessage(errorMessage, "error");
  }

  showMessage(message, type = "info") {
    if (!this.messageElement) return;

    this.messageElement.textContent = message;

    this.messageElement.classList.remove(
      "message--info",
      "message--success",
      "message--error",
    );
    this.messageElement.classList.add(`message--${type}`);

    this.messageElement.setAttribute("role", "status");
    this.messageElement.setAttribute("aria-live", "polite");

    this.messageElement.classList.remove("opacity-0");

    if (type !== "info") {
      if (this.timeout) {
        window.clearTimeout(this.timeout);
      }
      this.timeout = setTimeout(() => {
        this.clearMessage();
        this.timeout = false;
      }, 3000);
    }
  }

  clearMessage() {
    if (!this.messageElement) return;

    this.messageElement.classList.add("opacity-0");

    setTimeout(() => {
      this.messageElement.textContent = "";
      this.messageElement.classList.remove(
        "message--info",
        "message--success",
        "message--error",
      );
      this.messageElement.removeAttribute("role");
      this.messageElement.removeAttribute("aria-live");
    }, 500);
  }
}

customElements.define("product-form", ProductForm);

class ProductDescription extends HTMLElement {
  constructor() {
    super();

    document.addEventListener(
      "product-images:toggled",
      this.onProductImagesToggled.bind(this),
    );
  }

  onProductImagesToggled(event) {
    const { isToggled } = event.detail;

    if (isToggled) {
      this.classList.add("hidden");
    } else {
      this.classList.remove("hidden");
    }
  }
}
customElements.define("product-description", ProductDescription);

class ProductImages extends HTMLElement {
  constructor() {
    super();

    this.isToggled = this.dataset.toggled === "true";

    this.lastTouchTime = 0;

    // Store original sizes attributes
    this.originalSizes = new Map();
    this.images = this.querySelectorAll("img");
    this.images.forEach((img) => {
      const originalSizes = img.getAttribute("sizes");
      if (originalSizes) {
        this.originalSizes.set(img, originalSizes);
      }
    });

    this.addEventListener("click", this.onClickHandler.bind(this));
    this.addEventListener("touchstart", this.onTouchHandler.bind(this));
    this.addEventListener("touchend", this.onTouchHandler.bind(this));
  }

  onTouchHandler(event) {
    this.lastTouchTime = Date.now();
  }

  onClickHandler(event) {
    const timeSinceTouch = Date.now() - this.lastTouchTime;
    if (timeSinceTouch < 500) {
      return;
    }

    if (event.detail === 0) {
      return;
    }

    this.toggleClasses();
  }

  toggleClasses() {
    if (this.isToggled) {
      this.dataset.toggled = "false";

      const images = this.querySelectorAll(".image--extra-large");
      images.forEach((img) => {
        img.classList.replace("image--extra-large", "image--medium");

        // Restore original sizes attribute
        const originalSizes = this.originalSizes.get(img);
        if (originalSizes) {
          img.setAttribute("sizes", originalSizes);
        }
      });

      this.isToggled = false;
    } else {
      this.dataset.toggled = "true";

      const images = this.querySelectorAll(".image--medium");
      images.forEach((img) => {
        img.classList.replace("image--medium", "image--extra-large");

        // Set toggled sizes attribute
        img.setAttribute("sizes", "calc(100vw - (4px * 2))");
      });

      this.isToggled = true;
    }

    document.dispatchEvent(
      new CustomEvent("product-images:toggled", {
        bubbles: true,
        detail: {
          isToggled: this.isToggled,
          dataToggled: this.dataset.toggled,
        },
      }),
    );
  }

  setToggleState(shouldToggle) {
    if (shouldToggle !== this.isToggled) {
      this.toggleClasses();
    }
  }

  getToggleState() {
    return this.isToggled;
  }
}

customElements.define("product-images", ProductImages);
